export const PENDING_SALE_OVERRIDE_CODE_LENGTH = 6;

export type PendingSaleOverrideConsumeReason = 'missing' | 'mismatch' | 'unavailable';

export class PendingSaleOverrideConsumeError extends Error {
  readonly reason: PendingSaleOverrideConsumeReason;

  constructor(reason: PendingSaleOverrideConsumeReason) {
    super(reason);
    this.name = 'PendingSaleOverrideConsumeError';
    this.reason = reason;
  }
}

export type PendingSaleOverrideConsumeDecision =
  | { ok: true; nextCode: string }
  | { ok: false; reason: PendingSaleOverrideConsumeReason };

export function normalizePendingSaleOverrideCode(value: string | null | undefined): string {
  return (value || '').trim().toUpperCase();
}

/**
 * A code is consumed only when it matches the code still on file.
 * Wrong, short, or missing attempts leave that stored code in place.
 * The replacement must be a different code so the one just used cannot be entered again.
 */
export function decidePendingSaleOverrideConsume(input: {
  storedCode: string | null | undefined;
  enteredCode: string;
  nextCode: string;
}): PendingSaleOverrideConsumeDecision {
  const stored = normalizePendingSaleOverrideCode(input.storedCode);
  const entered = normalizePendingSaleOverrideCode(input.enteredCode);
  const nextCode = normalizePendingSaleOverrideCode(input.nextCode);

  if (!stored) {
    return { ok: false, reason: 'missing' };
  }

  if (entered.length < PENDING_SALE_OVERRIDE_CODE_LENGTH || entered !== stored) {
    return { ok: false, reason: 'mismatch' };
  }

  if (!nextCode || nextCode === stored) {
    return { ok: false, reason: 'unavailable' };
  }

  return { ok: true, nextCode };
}
